/**
 * "Card readers" line on a SUNMI T3 fleet card (T3 card setup, 2026-10-14).
 *
 * Reads the till's latest `session.heartbeat` from device-health (existing
 * admin endpoint, no new route) — a T3 sends `ttpState`, `ttpAppVersion` and
 * `backupState` as plain detail keys. Only mounted for T3 rows, so no other
 * card makes this request or shows this line.
 */
import React, { useEffect, useState } from 'react';
import { CreditCard } from 'lucide-react';
import { deviceHealthApi } from '../../../services/deviceHealthApi';
import { cardReadersLine } from '../../../components/pos/t3CardSetup';

const TONE = {
  ok: 'bg-green-600/10 text-green-700',
  warn: 'bg-amber-500/15 text-amber-700',
  bad: 'bg-rose-500/15 text-rose-700',
} as const;

export default function T3CardReadersLine({ deviceId }: { deviceId: string }) {
  const [line, setLine] = useState<ReturnType<typeof cardReadersLine> | undefined>(undefined);

  useEffect(() => {
    let live = true;
    deviceHealthApi.timeline(deviceId, '24h')
      .then((events) => {
        const hb = (events || [])
          .filter((e: any) => e.kind === 'session.heartbeat' && e.detail && ('ttpState' in e.detail || 'backupState' in e.detail || 'primaryState' in e.detail))
          .sort((a: any, b: any) => b.ts - a.ts)[0];
        if (live) setLine(hb ? cardReadersLine(hb.detail as Record<string, unknown>) : null);
      })
      .catch(() => { if (live) setLine(null); });
    return () => { live = false; };
  }, [deviceId]);

  return (
    <div className="flex min-h-[28px] items-center gap-2 rounded-xl bg-gray-50 px-2.5 py-1.5 text-[11.5px]" data-testid="t3-card-readers">
      <CreditCard size={13} className="shrink-0 text-gray-400" />
      <span className="w-[78px] shrink-0 text-[10px] font-bold uppercase tracking-wide text-gray-400">Card readers</span>
      {line === undefined ? (
        <span className="text-gray-400">…</span>
      ) : line ? (
        <span className={`rounded-full px-2 py-0.5 font-bold ${TONE[line.tone]}`}>{line.text}</span>
      ) : (
        <span className="italic text-gray-400">No card-reader report in the last 24h</span>
      )}
    </div>
  );
}
